// src/shared/ui/PageHeader.jsx
//
// The title + optional breadcrumb + right-aligned actions row repeated at
// the top of nearly every module's top-level pages, each hand-written
// slightly differently.
//
// Usage:
//   <PageHeader title="Deployments" actions={<Button>New deployment</Button>} />
//   <PageHeader title="Run #42" breadcrumbs={[{ label: "Deployments", href: "/golive" }, { label: "Run #42" }]} />

import PropTypes from "prop-types";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function PageHeader({ title, subtitle, breadcrumbs, actions, className = "" }) {
  return (
    <div className={`flex items-start justify-between gap-4 flex-wrap mb-6 ${className}`.trim()}>
      <div>
        {breadcrumbs?.length > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted mb-1.5">
            {breadcrumbs.map((crumb, i) => (
              <span key={i} className="flex items-center gap-1">
                {i > 0 && <ChevronRight size={12} />}
                {crumb.href ? (
                  <Link to={crumb.href} className="hover:text-ink hover:underline">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-ink font-medium">{crumb.label}</span>
                )}
              </span>
            ))}
          </div>
        )}
        <h1 className="text-xl font-semibold text-ink">{title}</h1>
        {subtitle && <p className="text-sm text-muted mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

PageHeader.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.node.isRequired, href: PropTypes.string })
  ),
  actions: PropTypes.node,
  className: PropTypes.string,
};
